// src/utils/autosaveDelay.js
//
// [AUTOSAVE-DEBOUNCE-STARVATION -- 2026-07-18]
// Pure scheduling decision for the consolidated autosave effect (App.jsx ~8989):
// trailing debounce with a max-wait, hand-rolled (lodash-debounce maxWait semantics).
//
// [THE BUG THIS FIXES]: the effect debounces 2000ms and its cleanup clearTimeout()s
// on EVERY dep-array change. Any sustained activity cadence faster than 2s pushes the
// flush out forever -- a player in active combat (HP clicks, casts, condition toggles
// every 1-2s) could go MINUTES with nothing persisted. Clean tab close was covered
// (beforeunload saves via stateRef), so the loss window was CRASH/kill/battery-death
// mid-burst: everything since the last pause. Measured indirectly by the sim harness:
// the localStorage autosave stayed at seeded values across 9+ consecutive actions
// (the stale snapshot that also produced the SIM-SLOT-PRE-POST-SOURCE-MIX false kill).
//
// [WHY THE SETTING IS THE MAX-WAIT]: settings.autosaveInterval (30s/1m/5m/10m,
// 0=disabled) is presented in SettingsPanel as "Every 30 seconds" etc., but its only
// runtime meaning was the on/off gate -- the label was a promise the code never kept.
// Using it as the max-wait makes it literally true in the worst case: bursts shorter
// than the interval still flush ~2s after they end (trailing debounce), and sustained
// activity flushes at least once per chosen interval. This is the reconciliation the
// board row asked for ("the 2000 at :9041 is hardcoded while autosaveInterval sits in
// the dep array -- reconcile while in there").
//
// Extracted (CommonJS-compatible ES export) so Jest asserts the production decision
// -- Rule 22: extract -> import -> assert, never an inline replica.

/**
 * Decide the setTimeout delay for the next autosave flush attempt.
 *
 * @param {number} now - current epoch ms
 * @param {number|null} lastFlushAttempt - epoch ms of the last time the flush
 *   callback actually RAN (attempt, not success -- a dedupe short-circuit still
 *   counts as "the flush had its chance", which prevents delay-0 hammering when
 *   nothing serializes differently). null = no attempt yet this burst.
 * @param {number} debounceMs - trailing debounce (the historical 2000)
 * @param {number} maxWaitMs - the max-wait ceiling (settings.autosaveInterval)
 * @returns {number} ms to schedule the flush at: debounceMs normally, the remaining
 *   time to the max-wait deadline when that is sooner, 0 when the deadline passed.
 */
export function autosaveDelay(now, lastFlushAttempt, debounceMs, maxWaitMs) {
  if (!maxWaitMs || maxWaitMs <= 0) return debounceMs; // no ceiling configured
  if (lastFlushAttempt === null || lastFlushAttempt === undefined) return debounceMs;
  const remaining = lastFlushAttempt + maxWaitMs - now;
  if (remaining <= 0) return 0; // deadline passed -- flush now, do not reschedule
  return Math.min(debounceMs, remaining);
}
